import { Sites } from '@ps/platform-message-framework/common/Sites';
import { ErrorCode, FeatureFlag, ResolverTag, Size as ScreenSize } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { CashierPath } from '@ps/web-pam-types';
import {
  StarsButton as Button,
  InfoTooltip,
  Input,
  Password,
  ScreenSizeContext,
  Toggle,
  Translation,
  useNavigate
} from '@ps/web-shared-components';
import { StarsInfo, createForm } from '@ps/web-shared-ui';
import { required } from '@ps/web-shared-validators';
import type { FormApi } from 'final-form';
import { isEmpty, isNil, omit, omitBy } from 'lodash';
import type { SyntheticEvent } from 'react';
import React, { useContext, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { styles as sharedStyles, useMitId } from '@swf/account';
import ButtonGroup from '@swf/components/ButtonGroup/ButtonGroup';
import ResolverLink from '@swf/components/ResolverLink/ResolverLink';
import eighteenPlusIcon from '@swf/core/assets/icons/eighteen-plus-icon.png';
import { EulaType } from '@swf/core/enums';
import useSearchParams from '@swf/core/hooks/useSearchParams';
import type { Dispatch, RootState } from '@swf/core/store';
import { select } from '@swf/core/store';

import ErrorMessage from '../ErrorMessage/ErrorMessage';

import {
  TXTCLI_Create_an_account_now,
  TXTCLI_Ensure_that_betting_and_gaming_ELL,
  TXTCLI_Forgot_Password___User_IDX,
  TXTCLI_If_you_or_someone_you_know_has_ELL,
  TXTCLI_Login,
  TXTCLI_Password,
  TXTCLI_User_ID,
  TXTCLI_XXPokerBrandXX_is_an_N0X_site,
  TXTWCL_Checking_this_box_means_you_ac_ELL,
  TXTWCL_DonXt_have_an_accountX_signup__ELL,
  TXTWCL_I_am_Xa_hrefXXN0XXeligibleX_aX_ELL_1,
  TXTWCL_Please_enter_your_PasswordX,
  TXTWCL_Please_enter_your_User_IDX,
  TXTWCL_SorryX_you_must_verify_your_id_ELL
} from './LoginForm.messages.json';
import styles from './LoginForm.scss';

interface FormValues {
  userId: string;
  password: string;
  extAuthToken?: string;
  error?: string;
  path?: string;
}

const Form = createForm<FormValues>();

interface Props {
  isCommunity?: boolean;
}

const LoginForm: React.FC<Props> = ({ isCommunity }) => {
  const history = useHistory();
  const { formatMessage } = useIntl();
  const { errorCode } = useSelector(select.auth.getResponse);
  const searchParams = useSearchParams<FormValues>();
  const prevStateRef = useRef<FormValues>(undefined);
  const formApiRef = useRef<FormApi<FormValues>>(undefined);
  const { current: prevState } = prevStateRef;
  const { current: formApi } = formApiRef;

  const siteId = useSelector(select.cms.getSiteId);
  const isCAON = siteId === Sites.CA_ON;
  const { greaterThan } = useContext(ScreenSizeContext);
  const loading = useSelector((rootState: RootState) => !!rootState.loading?.models.auth);
  const swcSignup = useFeature(FeatureFlag.SwcSignup);
  const bankID = useFeature(FeatureFlag.BankID);
  const navigate = useNavigate();
  const { mitIdError, mitIdData } = useMitId<FormValues>();

  const { auth, modal } = useDispatch<Dispatch>();

  useEffect(() => {
    // this fixes autologin issue on mobile. do not remove.
    let forceValidation: boolean = greaterThan(ScreenSize.Medium);

    // Show validation errors if opened from invalid header form
    if (
      searchParams &&
      searchParams !== prevStateRef.current &&
      !searchParams.extAuthToken &&
      !searchParams.error &&
      !isEmpty(omitBy(searchParams, isNil)) &&
      forceValidation
    ) {
      forceValidation = false;
      formApi?.submit();
    }

    prevStateRef.current = searchParams;
  }, [formApi, greaterThan, searchParams, prevState]);

  useEffect(() => {
    if (mitIdData) {
      formApiRef.current?.submit();
    }
  }, [mitIdData]);

  const openRegistration = (e: SyntheticEvent) => {
    e.preventDefault();

    switch (true) {
      case swcSignup:
        modal.close?.('/auth/login');
        navigate(CashierPath.Signup);
        break;
      case isCommunity:
        navigate('/');
        modal.enqueue({ path: `/auth/registration${bankID ? '/bankid' : ''}` });
        break;
      case bankID:
        history.push({ pathname: '/auth/registration/bankid' });
        break;
      default:
        history.push({ pathname: '/auth/registration' });
        break;
    }
  };

  const handlePasswordRecovery = () => {
    if (isCommunity) {
      navigate('/');
      modal.enqueue({ path: '/auth/password-recovery' });
      return;
    }

    history.push({ pathname: '/auth/password-recovery' });
  };

  const handleUserIdChange = () => {
    const licenseMismatch = errorCode === ErrorCode.LICENSE_MISMATCH;

    if (licenseMismatch) auth.clear();
  };

  return (
    <Form
      onSubmit={(values) => {
        auth.login(omit(values, 'iAmEligibleToPlay', 'path'));
      }}
      initialValues={mitIdData || searchParams}
      render={({ form, invalid }) => {
        formApiRef.current = form;
        const userIdFieldState = form.getFieldState('userId');
        const blockedByGeoIP = errorCode === ErrorCode.BLOCKED_BY_GEO_IP;
        const loginNotAllowed = errorCode === ErrorCode.LOGIN_MSG_NOT_ALLOWED;
        const accountClosed = errorCode === ErrorCode.ACCOUNT_CLOSED;

        const disableButton =
          blockedByGeoIP ||
          (!userIdFieldState?.dirtySinceLastSubmit && loginNotAllowed) ||
          (!userIdFieldState?.dirtySinceLastSubmit && siteId === Sites.GR && accountClosed);
        return (
          <>
            <ErrorMessage />
            {mitIdError && (
              <StarsInfo type="error" className={sharedStyles.message}>
                <Translation {...TXTWCL_SorryX_you_must_verify_your_id_ELL} />
              </StarsInfo>
            )}
            <Input
              autoFocus
              id="userId"
              label={TXTCLI_User_ID}
              placeholder={formatMessage(TXTWCL_Please_enter_your_User_IDX)}
              validators={[required(null)]}
              onBlur={() => null}
              onChange={handleUserIdChange}
            />
            <Password
              autoComplete="current-password"
              id="password"
              label={TXTCLI_Password}
              placeholder={formatMessage(TXTWCL_Please_enter_your_PasswordX)}
              validators={[required(null)]}
              onBlur={() => null}
            />
            {siteId === Sites.CA_ON && (
              <div className={styles.checkbox}>
                <Toggle
                  id={EulaType.IAmEligibleToPlay}
                  type="checkbox"
                  data-testid="i-am-eligible-to-play"
                  validators={[required(null)]}
                  label={{
                    ...TXTWCL_I_am_Xa_hrefXXN0XXeligibleX_aX_ELL_1,
                    values: {
                      a: (msg: React.ReactNode) => (
                        <ResolverLink
                          resolverTag={isCAON ? ResolverTag.EligibleEula : ResolverTag.MCEula}
                        >
                          {msg}
                        </ResolverLink>
                      )
                    }
                  }}
                />
                <InfoTooltip
                  content={[TXTWCL_Checking_this_box_means_you_ac_ELL]}
                  iconClassName={styles.infoTooltip}
                  tooltipContentWrapperClassName={styles.tooltipContentWrapper}
                />
              </div>
            )}
            <ButtonGroup>
              <Button
                block
                type="submit"
                loading={loading}
                disabled={invalid || disableButton}
                translation={TXTCLI_Login}
              />
              {[Sites.US_PA, Sites.US_NJ].includes(siteId) && (
                <p className={styles.regulatoryText}>
                  <Translation {...TXTCLI_If_you_or_someone_you_know_has_ELL} />
                </p>
              )}
              {siteId === Sites.BEL && (
                <p className={styles.regulatoryText}>
                  <Translation {...TXTCLI_Ensure_that_betting_and_gaming_ELL} />
                </p>
              )}
              <Button
                block
                variant="text"
                className={styles.buttonText}
                onClick={handlePasswordRecovery}
                translation={TXTCLI_Forgot_Password___User_IDX}
              />
              <Button
                variant="text"
                block
                onClick={openRegistration}
                values={{
                  accountBrand: [Sites.BEL, Sites.IND].includes(siteId) ? 'Stars' : 'PokerStars'
                }}
                translation={
                  [Sites.BEL, Sites.IND].includes(siteId) || isCommunity
                    ? TXTCLI_Create_an_account_now
                    : TXTWCL_DonXt_have_an_accountX_signup__ELL
                }
              />
            </ButtonGroup>
            {siteId === Sites.ROM && (
              <div className={styles.iconWrapper}>
                <img src={eighteenPlusIcon} width={20} height={20} alt="" />
                <Translation
                  tagName="span"
                  values={{ accountBrand: 'PokerStars', 0: 18 }}
                  {...TXTCLI_XXPokerBrandXX_is_an_N0X_site}
                />
              </div>
            )}
          </>
        );
      }}
    />
  );
};

export default LoginForm;
